import { get, isNumber } from 'lodash';
import EventBus, { Event, Unsubscriber } from '@/core/event-bus';
import { PlayerState } from '@/core/player-state';

export function playHandler(eventBus: EventBus, video: HTMLVideoElement, initController: () => Promise<void>): Unsubscriber {
    return eventBus.on(
        Event.play,
        () => play(video, initController)
    );
}

async function play(video: HTMLVideoElement, initController: () => Promise<void>) {
    await initController();
    await video.play().catch((error) => {
        console.error('[ActionHandlers] could not play', error);
    });
}

export function pauseHandler(eventBus: EventBus, video: HTMLVideoElement, setPauseExpected: (val:boolean)=>void): Unsubscriber {
    console.debug('[ActionHandlers] pauseHandler');
    return eventBus.on(
        Event.pause,
        () => {
            console.debug('[ActionHandlers] event bus on Event.pause');
            setPauseExpected(true);
            video.pause();
        }
    );
}

export function enterFullScreenHandler(eventBus: EventBus, updateState: (partialState: Partial<PlayerState>) => void): Unsubscriber {
    return eventBus.on(
        Event.enterFullScreen,
        () => updateState({ isVideoElementFullScreen: true }));
}

export function exitFullScreenHandler(eventBus: EventBus, updateState: (partialState: Partial<PlayerState>) => void): Unsubscriber {
    return eventBus.on(
        Event.exitFullScreen,
        () => updateState({ isVideoElementFullScreen: false }));
}

export function enterNativeFullScreenHandler(eventBus: EventBus, playerElement: Element): Unsubscriber {
    return eventBus.on(
        Event.enterNativeFullScreen,
        async () => {
            try {
                await playerElement.requestFullscreen();
            } catch (err) {
                eventBus.emit(Event.error, err, 'native-fullscreen');
            }
        });
}

export function exitNativeFullScreenHandler(eventBus: EventBus): Unsubscriber {
    return eventBus.on(
        Event.exitNativeFullScreen,
        () => document.exitFullscreen());
}

export function alwaysShowControlBarHandler(eventBus: EventBus, updateState: (partialState: Partial<PlayerState>) => void): Unsubscriber {
    return eventBus.on(
        Event.alwaysShowControlBar,
        (payload) => {
            const alwaysShowControlBar = get(payload, 'alwaysShowControlBar') as boolean;
            updateState({ alwaysShowControlBar } as Partial<PlayerState>);
        });
}

export function seekHandler(eventBus: EventBus, video: HTMLVideoElement, updateState: (partialState: Partial<PlayerState>, event: Event) => void): Unsubscriber {
    return eventBus.on(
        Event.seek,
        (payload) => {
            seek(video, payload as Record<string, unknown>, updateState);
        }
    );
}

function seek(video: HTMLVideoElement, payload:Record<string, unknown>, updateState: (partialState: Partial<PlayerState>, event: Event) => void): void {
    let secs = get(payload, 'seconds') as number;
    if (!isNumber(secs)) {
        return;
    }
    secs = Math.min(Math.max(secs, 0), video.duration || secs);
    // Setting `currentTime` in the HTMLVideoElement is throttled. We still want the state to be updated on every `seek` request
    updateState({ videoElapsedSecs: secs }, Event.seek);
    video.currentTime = secs;
}

export function muteHandler(eventBus: EventBus, setMuted: (value: boolean) => void): Unsubscriber {
    return eventBus.on(
        Event.mute,
        () => setMuted(true)
    );
}

export function unmuteHandler(eventBus: EventBus, setMuted: (value: boolean) => void): Unsubscriber {
    return eventBus.on(
        Event.unmute,
        () => setMuted(false)
    );
}

export function changeVolumeHandler(eventBus: EventBus, video: HTMLVideoElement, updateState: (partialState: Partial<PlayerState>) => void): Unsubscriber {
    return eventBus.on(
        Event.changeVolume,
        payload => {
            let volume = get(payload, 'volume');
            if (isNumber(volume)) {
                volume = Math.min(Math.max(volume, 0), 1);
                video.volume = volume;
                updateState({ volume });
            }
        }
    );
}
